import { WeighingTicketData } from '@/components/WeighingTicket';
import { Ticket } from './ticketsService';
import { generatePrintHTML, generatePDF, generateEmailTemplate, blobToBase64 } from '@/utils/ticketUtils';
import { convertPeseeToTicketData } from '@/utils/ticketDataConverter';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { generateRefoulementPDF, printPDF, downloadPDF } from './pdfService';
import refoulementService from './refoulementService';
import emailService from './emailService';
import apiService from './api';

export interface TicketActionOptions {
  onSuccess?: (action: string) => void;
  onError?: (error: string) => void;
}

class TicketActionsService {
  /**
   * Imprime un ticket
   */
  async printTicket(
    ticketData: WeighingTicketData | Ticket,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      // Vérifier si c'est un ticket de refoulement
      if (this.isRefoulementTicket(ticketData)) {
        await this.printRefoulementTicket(ticketData, options);
        return;
      }

      const data = this.normalizeTicketData(ticketData);
      const printHTML = generatePrintHTML(data);
      // Méthode 1: Essayer d'abord window.open avec plus d'options
      let printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no');
      
      if (!printWindow) {
        // Méthode 2: Utiliser la méthode alternative avec iframe
        console.warn('Popup bloqué, utilisation de la méthode alternative');
        await this.printTicketAlternative(ticketData, options);
        return;
      }

      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
      
      options.onSuccess?.('impression');
     
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'impression';
     
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Exporte un ticket en PDF
   */
  async exportTicketPDF(
    ticketData: WeighingTicketData | Ticket,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      // Vérifier si c'est un ticket de refoulement
      if (this.isRefoulementTicket(ticketData)) {
        await this.exportRefoulementTicket(ticketData, options);
        return;
      }

      const data = this.normalizeTicketData(ticketData);
      await generatePDF(data);
      options.onSuccess?.('export');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'export PDF';
     
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Envoie un ticket par email via le client mail
   */
  async sendTicketEmail(
    ticketData: WeighingTicketData | Ticket,
    recipients: string,
    subject?: string,
    message?: string,
    cc?: string,
    bcc?: string,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      // Vérifier si c'est un ticket de refoulement
      if (this.isRefoulementTicket(ticketData)) {
        await this.sendRefoulementEmail(ticketData, recipients, subject, message, cc, bcc, options);
        return;
      }

      // Utiliser l'envoi depuis l'application par défaut
      const recipientsList = recipients.split(',').map(email => email.trim()).filter(email => email);
      const ccList = cc ? cc.split(',').map(email => email.trim()).filter(email => email) : [];
      const bccList = bcc ? bcc.split(',').map(email => email.trim()).filter(email => email) : [];
      
      await this.sendTicketEmailFromApp(
        ticketData,
        recipientsList,
        ccList,
        bccList,
        subject,
        message,
        options
      );
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'envoi d\'email';
     
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Envoie un ticket par email depuis l'application
   */
  async sendTicketEmailFromApp(
    ticketData: WeighingTicketData | Ticket,
    recipients: string[],
    cc: string[] = [],
    bcc: string[] = [],
    subject?: string,
    message?: string,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      const data = this.normalizeTicketData(ticketData);
      const emailSubject = subject || `Ticket de Pesée - ${data.code}`;
      const emailMessage = message || generateEmailTemplate(data);
      
      // Générer le PDF avec la même logique que generatePDF
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = generatePrintHTML(data);
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '210mm'; // A4 width
      tempDiv.style.backgroundColor = 'white';
      
      // Ajouter l'élément au DOM temporairement
      document.body.appendChild(tempDiv);
      
      // Attendre que les images se chargent
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Convertir le HTML en canvas
      const canvas = await html2canvas(tempDiv, {
        useCORS: true, // Permettre les images cross-origin
        allowTaint: true,
        background: '#ffffff',
        width: 794, // A4 width in pixels at 96 DPI
        height: tempDiv.scrollHeight,
        logging: false // Désactiver les logs
      });
      
      // Créer le PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      // Ajouter la première page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Ajouter des pages supplémentaires si nécessaire
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Convertir le PDF en blob
      const pdfBlob = pdf.output('blob');
      
      // Nettoyer l'élément temporaire
      document.body.removeChild(tempDiv);
      
      // Préparer les données pour l'envoi
      const emailDataToSend = {
        weighing_id: parseInt(data.id || '0'),
        recipients: recipients,
        cc: cc,
        bcc: bcc,
        subject: emailSubject,
        message: emailMessage,
        pdf_content: await blobToBase64(pdfBlob),
        pdf_filename: `ticket_pesee_${data.code}.pdf`
      };

      // Envoyer l'email via l'API
      await apiService.post('/weighing/api/emails/send_email/', emailDataToSend);
      
      options.onSuccess?.('email');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'envoi d\'email depuis l\'application';
      console.error('❌ Erreur lors de l\'envoi d\'email depuis l\'application:', error);
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Copie les informations du ticket dans le presse-papiers
   */
  async copyTicketInfo(
    ticketData: WeighingTicketData | Ticket,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      const data = this.normalizeTicketData(ticketData);
      const ticketInfo = `TICKET DE PESÉE
================

Référence: ${data.code}
Date: ${data.weighingDate || new Date().toLocaleDateString('fr-FR')}
Produit: ${data.product}
Véhicule: ${data.vehicle}
Chauffeur: ${data.driver}
Poids brut: ${data.grossWeight.toLocaleString()} kg
Poids net: ${data.netWeight.toLocaleString()} kg
Fournisseur: ${data.supplierIdentity || 'N/A'}
Client: ${data.clientCode || 'N/A'}

Observations: ${data.observations || 'Aucune'}`;

      await navigator.clipboard.writeText(ticketInfo);
      options.onSuccess?.('copie');
     
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la copie';
      
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Normalise les données de ticket (WeighingTicketData ou Ticket) vers WeighingTicketData
   * Utilise maintenant la fonction centralisée convertPeseeToTicketData
   */
  private normalizeTicketData(ticketData: WeighingTicketData | Ticket): WeighingTicketData {
    if ('code' in ticketData && 'grossWeight' in ticketData) {
      // C'est déjà un WeighingTicketData
      return ticketData as WeighingTicketData;
    }

    // C'est un Ticket, on le convertit en utilisant la fonction centralisée
    return convertPeseeToTicketData(ticketData as any);
  }

  /**
   * Méthode alternative d'impression qui fonctionne même avec les popups bloqués
   */
  async printTicketAlternative(
    ticketData: WeighingTicketData | Ticket,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      const data = this.normalizeTicketData(ticketData);
      const printHTML = generatePrintHTML(data);
      
      // Créer un iframe caché pour l'impression
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      iframe.style.border = 'none';
      
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(printHTML);
        iframeDoc.close();
        
        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          
          // Nettoyer après impression
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        };
      }
      
      options.onSuccess?.('impression');
    } catch (error) {
      console.error('Erreur lors de l\'impression alternative:', error);
      options.onError?.(error instanceof Error ? error.message : 'Erreur lors de l\'impression');
      throw error;
    }
  }


  /**
   * Vérifie si c'est un ticket de refoulement
   */
  private isRefoulementTicket(ticketData: WeighingTicketData | Ticket): boolean {
    if ('statut' in ticketData && 'reference' in ticketData) {
      return (ticketData as Ticket).statut === 'REFOULEE';
    }
    return false;
  }

  /**
   * Imprime un ticket de refoulement
   */
  private async printRefoulementTicket(
    ticketData: WeighingTicketData | Ticket,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      // Récupérer les données de refoulement
      const refoulementData = await this.getRefoulementData(ticketData);
      
      // Générer le PDF de refoulement
      const pdfDoc = await generateRefoulementPDF(refoulementData);
      
      // Imprimer le PDF
      printPDF(pdfDoc);
      
      options.onSuccess?.('impression refoulement');
     
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'impression du refoulement';
     
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Exporte un ticket de refoulement en PDF
   */
  private async exportRefoulementTicket(
    ticketData: WeighingTicketData | Ticket,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      // Récupérer les données de refoulement
      const refoulementData = await this.getRefoulementData(ticketData);
      
      // Générer le PDF de refoulement
      const pdfDoc = await generateRefoulementPDF(refoulementData);
      
      // Télécharger le PDF
      const ticket = ticketData as Ticket;
      const filename = `refoulement_${ticket.reference || ticket.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      downloadPDF(pdfDoc, filename);
      
      options.onSuccess?.('export refoulement');
     
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'export PDF du refoulement';
      console.error('❌ Erreur lors de l\'export PDF du refoulement:', error);
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Récupère les données de refoulement pour un ticket
   */
  private async getRefoulementData(ticketData: WeighingTicketData | Ticket): Promise<any> {
    try {
      // Récupérer les refoulements pour cette pesée
      const refoulementsResponse = await refoulementService.getRefoulements();
      const refoulements = refoulementsResponse.results || refoulementsResponse;
      const refoulement = refoulements.find((r: any) => r.pesee_reference === (ticketData as Ticket).reference);
      
      if (!refoulement) {
        throw new Error('Aucun refoulement trouvé pour cette pesée');
      }

      // Préparer les données pour le PDF
      const ticket = ticketData as Ticket;
      const peseeData = {
        reference: ticket.reference || '',
        grossWeight: Number(ticket.poids_brut) || 0,
        tareWeight: Number(ticket.poids_tare) || 0,
        netWeight: Number(ticket.poids_net) || 0,
        vehicle: ticket.camion_immatriculation || '',
        driver: ticket.chauffeur_nom || '',
        product: ticket.produit_nom || '',
        site: 'Site de pesée', // À adapter selon vos données
        tenant: 'Conseil du Coton et de l\'Anacarde',
        grossAt: ticket.date_heure || '',
        tareAt: ticket.date_heure || '',
        grossUser: 'Agent de pesée', // À adapter selon vos données
        tareUser: 'Agent de pesée' // À adapter selon vos données
      };

      return {
        numero_refoulement: refoulement.numero_refoulement,
        date_refoulement: refoulement.date_refoulement,
        pesee_reference: refoulement.pesee_reference,
        agent_refoulement: refoulement.agent_refoulement,
        motifs: refoulement.motifs || [],
        observations: refoulement.observations || '',
        peseeData
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des données de refoulement:', error);
      throw error;
    }
  }

  /**
   * Envoie un ticket de refoulement par email
   */
  private async sendRefoulementEmail(
    ticketData: WeighingTicketData | Ticket,
    recipients: string,
    subject?: string,
    message?: string,
    cc?: string,
    bcc?: string,
    options: TicketActionOptions = {}
  ): Promise<void> {
    try {
      // Récupérer les données de refoulement
      const refoulementData = await this.getRefoulementData(ticketData);
      
      // Générer le PDF de refoulement
      const pdfDoc = await generateRefoulementPDF(refoulementData);
      
      // Convertir le PDF en base64 pour l'email
      const pdfBlob = pdfDoc.output('blob');
      const pdfBase64 = await blobToBase64(pdfBlob);
      
      // Préparer l'email
      const ticket = ticketData as Ticket;
      const emailSubject = subject || `Ticket de Refoulement - ${ticket.reference}`;
      const emailMessage = message || this.generateRefoulementEmailTemplate(refoulementData);
      
      // Envoyer l'email avec le PDF en pièce jointe
      await emailService.sendEmail({
        weighing_id: parseInt(ticket.id.toString()),
        recipients: [recipients],
        cc: cc ? [cc] : undefined,
        bcc: bcc ? [bcc] : undefined,
        subject: emailSubject,
        message: emailMessage,
        pdf_content: pdfBase64,
        pdf_filename: `refoulement_${ticket.reference}.pdf`
      });
      
      options.onSuccess?.('email refoulement');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'envoi de l\'email du refoulement';
      console.error('❌ Erreur lors de l\'envoi de l\'email du refoulement:', error);
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Génère un template d'email pour les refoulements
   */
  private generateRefoulementEmailTemplate(refoulementData: any): string {
    return `Bonjour,

Veuillez trouver ci-joint le ticket de refoulement ${refoulementData.numero_refoulement} pour la pesée ${refoulementData.pesee_reference}.

Détails du refoulement :
- Numéro de refoulement : ${refoulementData.numero_refoulement}
- Date de refoulement : ${new Date(refoulementData.date_refoulement).toLocaleDateString('fr-FR')}
- Agent de refoulement : ${refoulementData.agent_refoulement}
- Motifs : ${refoulementData.motifs.map((m: any) => m.libelle || m).join(', ')}
- Observations : ${refoulementData.observations || 'Aucune'}

Détails de la pesée :
- Référence : ${refoulementData.peseeData.reference}
- Véhicule : ${refoulementData.peseeData.vehicle}
- Chauffeur : ${refoulementData.peseeData.driver}
- Produit : ${refoulementData.peseeData.product}
- Poids net : ${refoulementData.peseeData.netWeight.toLocaleString()} kg

Le ticket PDF de refoulement est joint à cet email.

Cordialement,
Service de Pesée - Conseil du Coton et de l'Anacarde`;
  }
}

export default new TicketActionsService();
